import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

/**
 * Free-disk-space dialog for the offline audiobook download. Three modes:
 *   - 'block'   : not enough space — single [Got it] button, download refused.
 *   - 'warn'    : enough space but tight — [Cancel] / [Download].
 *   - 'nospace' : the download ran out of space mid-way; the partial is kept and
 *                 playable — [Keep what downloaded] / [Try again later] (both dismiss).
 *
 * Sizes are shown in DECIMAL (1000-base) MB/GB to match what Android's own
 * Storage panel and Google Play show the user.
 *
 * Controlled: pass `state` (null = closed) plus onCancel / onConfirm.
 *   block/warn state: { mode, bookTitle, neededBytes, freeBytes }
 *   nospace state:    { mode, done, total }
 */

// Decimal (1000-base) human size, 1 decimal place. "446.8 MB" below 1000 MB,
// "1.2 GB" at/above.
export function formatSize(bytes) {
  if (typeof bytes !== 'number' || !isFinite(bytes) || bytes < 0) return '—';
  const mb = bytes / 1e6;
  if (mb < 1000) return `${mb.toFixed(1)} MB`;
  return `${(bytes / 1e9).toFixed(1)} GB`;
}

export default function SpaceCheckDialog({ state, onCancel, onConfirm }) {
  const open = Boolean(state);
  const mode = state?.mode;

  let title = '';
  let body = null;
  let buttons = null;

  if (mode === 'block') {
    title = 'Not enough space to download';
    body = (
      <p className="text-sm text-slate-700 leading-relaxed">
        "{state.bookTitle}" needs <strong>{formatSize(state.neededBytes)}</strong> but your
        phone only has <strong>{formatSize(state.freeBytes)}</strong> free. Free up some space
        to download, or listen online instead.
      </p>
    );
    buttons = (
      <Button className="w-full bg-gradient-to-r from-slate-500 to-teal-600 font-semibold text-white hover:from-teal-600 hover:to-teal-700" onClick={onCancel}>
        Got it
      </Button>
    );
  } else if (mode === 'warn') {
    title = 'Your phone is almost full';
    body = (
      <p className="text-sm text-slate-700 leading-relaxed">
        This download will use <strong>{formatSize(state.neededBytes)}</strong> of your{' '}
        <strong>{formatSize(state.freeBytes)}</strong> free space. Your phone will be nearly
        full afterwards. Download anyway?
      </p>
    );
    buttons = (
      <>
        <Button variant="outline" className="flex-1" onClick={onCancel}>Cancel</Button>
        <Button className="flex-1 bg-gradient-to-r from-slate-500 to-teal-600 font-semibold text-white hover:from-teal-600 hover:to-teal-700" onClick={onConfirm}>
          Download
        </Button>
      </>
    );
  } else if (mode === 'nospace') {
    title = 'Your phone ran out of space';
    body = (
      <p className="text-sm text-slate-700 leading-relaxed">
        Downloaded <strong>{state.done} of {state.total} chapters</strong> before your phone
        ran out of space. What you have will keep playing — free up space and try again when
        you can.
      </p>
    );
    buttons = (
      <Button className="w-full bg-gradient-to-r from-slate-500 to-teal-600 font-semibold text-white hover:from-teal-600 hover:to-teal-700" onClick={onCancel}>
        Got it
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onCancel?.(); }}>
      <DialogContent className="max-w-md rounded-2xl p-6">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-slate-900">{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {body}
          <div className="flex gap-2 pt-2">{buttons}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
